import { Injectable } from '@angular/core';
import { Intercom } from 'ng-intercom';
import { AppConfig } from '../../../../environments/environment';
import { Subject } from 'rxjs';
import { ElectronService } from '../../../core/services';
import { Logger, LoggerService } from '../../../core/services/logger';

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  public onHide$: Subject<void> = new Subject();
  public onShow$: Subject<void> = new Subject();
  public isIntercomBooted: boolean = false;

  private intercomLoadListenerAttached: boolean = false;
  private intercomListenersRegistered: boolean = false;
  private messengerObserver: MutationObserver | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private iframeLoadHandler: (() => void) | null = null;

  private logger: Logger;

  constructor(
    private intercom: Intercom,
    private electronService: ElectronService,
    private loggerService: LoggerService
  ) {
    this.logger = loggerService.getLoggerInstance('ChatService');
    this.intercom.boot({
      app_id: AppConfig.INTERCOM_ID,
      hide_default_launcher: true,
      widget: {
        "activator": "#intercom"
      }
    });
    this.isIntercomBooted = true;
    this.registerIntercomListeners();
  }

  private registerIntercomListeners(): void {
    if (this.intercomListenersRegistered) return;
    this.intercomListenersRegistered = true;

    this.intercom.onHide(() => {
      this.onHide$.next();
    });

    this.intercom.onShow(() => {
      this.onShow$.next();
      if (this.electronService.isElectron) {
        this.applyMessengerFix();
      }
    });
  }

  // Electron 16 (Chromium 96) doesn't support dvh/dvw units — Intercom's messenger
  // container collapses. Inject a % fallback whenever the messenger is shown.
  private applyMessengerFix(): void {
    const injectInner = (iframe: HTMLIFrameElement) => {
      try {
        if (!iframe?.contentDocument?.head) return;
        if (iframe.contentDocument.getElementById('intercom-electron-fix-inner')) return;
        const style = iframe.contentDocument.createElement('style');
        style.id = 'intercom-electron-fix-inner';
        // dvh/dvw unsupported in Electron 16 (Chromium 96); fall back to %
        style.textContent = `[data-testid="floating-frame-dnd-target"] { height: 100% !important; }`;
        iframe.contentDocument.head.appendChild(style);
      } catch (error) {
        this.logger.warn('Failed to inject Intercom messenger fix:', error);
      }
    };

    const setupLoadListener = (iframe: HTMLIFrameElement) => {
      if (this.intercomLoadListenerAttached) return;
      this.intercomLoadListenerAttached = true;
      this.iframe = iframe;
      // Re-inject on every iframe navigation (e.g. "Find an Answer" loads a new document)
      this.iframeLoadHandler = () => injectInner(iframe);
      iframe.addEventListener('load', this.iframeLoadHandler);
    };

    const iframe = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement;
    if (iframe) {
      injectInner(iframe);
      setupLoadListener(iframe);
      return;
    }

    // Already observing — avoid creating multiple observers on rapid onShow calls
    if (this.messengerObserver) return;

    // Iframe not in DOM yet — Intercom creates it lazily on first show.
    this.messengerObserver = new MutationObserver(() => {
      const el = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement;
      if (!el) return;
      this.messengerObserver?.disconnect();
      this.messengerObserver = null;
      injectInner(el);
      setupLoadListener(el);
    });
    this.messengerObserver.observe(document.body, { childList: true, subtree: true });
  }

  public boot() {
    this.intercom.boot({
      app_id: AppConfig.INTERCOM_ID,
      hide_default_launcher: true,
      widget: {
        "activator": "#intercom"
      }
    });
    this.isIntercomBooted = true;
    this.registerIntercomListeners();
  }

  public bootWithEmail(email: string) {
    this.intercom.boot({
      app_id: AppConfig.INTERCOM_ID,
      hide_default_launcher: true,
      widget: {
        "activator": "#intercom"
      },
      email: email
    });
    this.isIntercomBooted = true;
    this.registerIntercomListeners();
  }

  public show() {
    this.intercom.show();
  }

  public hide() {
    this.intercom.hide();
  }

  public shutDown() {
    this.intercom.shutdown();
    this.isIntercomBooted = false;
    this.intercomLoadListenerAttached = false;
    if (this.iframe && this.iframeLoadHandler) {
      this.iframe.removeEventListener('load', this.iframeLoadHandler);
      this.iframe = null;
      this.iframeLoadHandler = null;
    }
    this.messengerObserver?.disconnect();
    this.messengerObserver = null;
    this.intercomListenersRegistered = false;
  }

  public update(data: any) {
    this.intercom.update(data);
  }

  public updateLoggedEmail(email: string) {
    this.update({
      email: email
    });
  }
}